import React, { useState } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    styled,
    Card,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import { useNavigate } from "react-router-dom";
import { useFormik } from 'formik';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';

import { useSchedulers, schedulerTypes, targetTypes, schedulerConfigs } from '../../../hooks/api/useSchedulers';
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';

import { initialValues, validationSchema } from "../../../schema/scheduler/taskSchema";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function NewTask({
    routeType
}) {

    const { promptMessage } = useSystemPrompt();
    const { isLoading, create } = useSchedulers({ schedulerType: schedulerTypes.schedulerTask, targetType: targetTypes.schedulerTask });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.scheduler.id, scopeTypes.scheduler.task.id)

    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const onSubmit = async (currentValues) => {

        try {

            let data = {
                scheduler_task: {
                    integration_id: currentValues?.integrationId,
                    task_key: currentValues?.taskId,
                    name: currentValues?.taskName,
                    description: currentValues?.description,
                    status: currentValues?.status ? 1 : 0,
                }
            }

            const response = await create({ data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("settings/schedulers"));

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const getAvailableTasks = () => {

        return schedulerConfigs.getSchedulerTasks(
            schedulerConfigs.schedulerIntegrations,
            values?.integrationId ? values.integrationId : ""
        )
    }

    const RenderForm = () => {
        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={4} textAlign={"left"}>

                            <CommonSectionHeader
                                title={'Information'}
                                subTitle={"Provide details of the profile"}
                            />

                        </Grid>
                        <Grid item xs={12} sm={12} md={8}>
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={12} md={6} textAlign="left">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="status"
                                                checked={Boolean(values.status)}
                                                onChange={(event, checked) => {
                                                    setFieldValue("status", checked);
                                                }}
                                            />
                                        }
                                        label={
                                            Boolean(values.status) ? "Enabled" : "Disabled"
                                        }
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    {/* Space down */}
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>

                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={Boolean(errors?.integrationId)}
                                    >
                                        <InputLabel id="integrationId">Integration</InputLabel>
                                        <Select
                                            fullWidth
                                            labelId="integrationId"
                                            label="Integration"
                                            name={"integrationId"}
                                            onChange={(e) => {
                                                setFieldValue(`taskId`, '')
                                                setFieldValue(`integrationId`, e.target.value)
                                            }}
                                            value={values?.integrationId ? values.integrationId : ""}
                                        >
                                            {
                                                schedulerConfigs.schedulerIntegrationTypes.map((i, index) => {
                                                    return (
                                                        <MenuItem
                                                            key={index}
                                                            value={i.id}
                                                        >
                                                            {i.name} ({i.id})
                                                        </MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {errors?.integrationId}
                                        </FormHelperText>
                                    </FormControl>

                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>

                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={Boolean(errors?.taskId)}
                                    >
                                        <InputLabel id="taskId">Available Task</InputLabel>
                                        <Select
                                            fullWidth
                                            labelId="taskId"
                                            label="Available Tasks"
                                            name={"taskId"}
                                            onChange={handleChange}
                                            value={values?.taskId ? values.taskId : ""}
                                        >
                                            {
                                                getAvailableTasks().map((i, index) => {
                                                    return (
                                                        <MenuItem
                                                            key={index}
                                                            value={i.id}
                                                        >
                                                            {i.name} ({i.id})
                                                        </MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {errors?.taskId}
                                        </FormHelperText>
                                    </FormControl>

                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"taskName"}
                                        label="Name"
                                        type="text"
                                        variant="outlined"
                                        value={values.taskName}
                                        onChange={handleChange}
                                        error={touched.taskName && Boolean(errors.taskName)}
                                        helperText={touched.taskName && errors.taskName}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"description"}
                                        label="Description"
                                        type="text"
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        value={values.description}
                                        onChange={handleChange}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            Save Changes
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.newTask })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle="New Scheduler Task"
                breadcrumbs={[
                    {
                        displayText: "Scheduler Tasks",
                        props: {
                            href: getRoute("settings/schedulers")
                        }
                    },
                    {
                        displayText: "New Scheduler Task",
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageNew.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}